import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui";
import React from "react";
import { toast } from "react-toastify";
import { FormGroup } from "~/components/common/parts/form/FormGroup";
import { FormLabel } from "~/components/common/parts/form/FormLabel";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { Modal } from "~/components/common/parts/modal/Modal";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { CompanySetting } from "~/models/CompanySetting";
import { destroy } from "~/utils/api/admin/CompanySetting";
import { isSuccess } from "~/utils/api/api_handler";
import Styles from "./CompanyDetailModal.module.scss";

type Props = DeepReadonly<{
	companySetting: CompanySetting;
	handleClose: () => void;
	handleMutate: () => Promise<void>;
	isOpen: boolean;
}>;

export const CompanyDetailModal: React.FC<Props> = ({
	isOpen,
	handleClose,
	companySetting,
	handleMutate
}: Props) => {
	const {
		isOpen: openConfirm,
		handleOpen: handleOpenConfirm,
		handleClose: handleCloseConfirm
	} = useOpenHandler();
	const handleAcceptDelete = React.useCallback(async (): Promise<void> => {
		const response = await destroy(companySetting);
		if (isSuccess(response)) {
			await handleMutate();
			handleClose();
			toast.success("削除しました");
			return;
		}
		toast.error(`エラーが発生しました: ${response.message}`);
	}, [handleMutate, companySetting, handleClose]);
	return (
		<Modal handleClose={handleClose} isOpen={isOpen} title="会社申込情報">
			<FormGroup>
				<FormLabel>プラン</FormLabel>
				{companySetting.marketPlan === "single" && "単発発注"}
				{companySetting.marketPlan === "multiple" && "まとめて発注割引"}
			</FormGroup>
			<FormGroup>
				<FormLabel>会社名</FormLabel>
				{companySetting.name}
			</FormGroup>
			<FormGroup>
				<FormLabel>代表者名</FormLabel>
				{`${companySetting.ceoLastName} ${companySetting.ceoFirstName}`}
			</FormGroup>
			<FormGroup>
				<FormLabel>管理戸数</FormLabel>
				{companySetting.managedUnitNumber}
			</FormGroup>
			<div className={Styles.DeleteButtonArea}>
				<Button height="small" onClick={handleOpenConfirm} variant="danger">
					アカウントを削除
				</Button>
				<ConfirmModal
					confirmType="delete"
					handleAccept={handleAcceptDelete}
					handleCancel={handleCloseConfirm}
					isOpen={openConfirm}
				>
					アカウントを削除してよろしいですか？
				</ConfirmModal>
			</div>
		</Modal>
	);
};
